import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styles from './BurnInUpload.module.css';
import GoToDashboardButton from './GoToDashboardButton';
import { buildApiUrl, fetchCsrfToken } from '../api/client';
import { describeRequestError, summarizeUploadResults } from '../utils/userFacingErrors';

const getApiBase = () =>
  process.env.REACT_APP_API_BASE ||
  (window.location.port === '3000' ? 'http://localhost:3001' : '');

const BurnInUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [activeFileName, setActiveFileName] = useState('');
  const [processedFiles, setProcessedFiles] = useState(0);
  const [recentUploads, setRecentUploads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [duplicateCount, setDuplicateCount] = useState(0);

  const fileInputRef = useRef(null);

  const loadUploads = async () => {
    try {
      const apiBase = getApiBase();
      const [uploadsResponse, summaryResponse] = await Promise.all([
        axios.get(`${apiBase}/api/burn-in/uploads`, { withCredentials: true }),
        axios.get(`${apiBase}/api/burn-in/summary`, { withCredentials: true }),
      ]);

      setRecentUploads(uploadsResponse.data || []);
      setSummary(summaryResponse.data || null);
    } catch (error) {
      console.error('Failed to load burn-in uploads:', error);
    }
  };

  useEffect(() => {
    loadUploads();
  }, []);

  useEffect(() => {
    if (!selectedUploadId) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closePreviewModal();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedUploadId]);

  const loadUploadPreview = async (uploadId) => {
    try {
      const apiBase = getApiBase();
      const response = await axios.get(`${apiBase}/api/burn-in/uploads/${uploadId}`, { withCredentials: true });
      setSelectedUploadId(uploadId);
      setPreviewRows(response.data.previewRows || []);
    } catch (error) {
      console.error('Failed to load burn-in preview:', error);
    }
  };

  const closePreviewModal = () => {
    setSelectedUploadId(null);
    setPreviewRows([]);
  };

  const updateFileProgress = (completedFiles, fileFraction, totalFiles) => {
    const totalProgress = ((completedFiles + fileFraction) / totalFiles) * 100;
    setUploadProgress(Math.min(99, Math.max(1, Math.round(totalProgress))));
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    setFiles(Array.from(event.target.files || []));
    setUploadStatus(null);
    setUploadProgress(0);
    setActiveFileName('');
    setProcessedFiles(0);
    setDuplicateCount(0);
  };

  const handleDeleteFile = (index) => {
    if (isUploading) return;
    setFiles(files.filter((_, currentIndex) => currentIndex !== index));
  };

  const selectedUpload = recentUploads.find((item) => item.id === selectedUploadId) || null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!files.length || isUploading) return;

    const queuedFiles = [...files];
    const responses = [];
    let duplicates = 0;

    try {
      setIsUploading(true);
      setUploadStatus(null);
      setUploadProgress(1);
      setProcessedFiles(0);
      setDuplicateCount(0);

      const csrfToken = await fetchCsrfToken();

      for (let index = 0; index < queuedFiles.length; index += 1) {
        const file = queuedFiles[index];
        const formData = new FormData();
        formData.append('files', file);
        setActiveFileName(file.name);
        setProcessedFiles(index);

        const response = await axios.post(buildApiUrl('/api/burn-in/upload'), formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'X-CSRF-Token': csrfToken },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || file.size || 1;
            const bytesFraction = Math.min(progressEvent.loaded / total, 1);
            updateFileProgress(index, bytesFraction * 0.85, queuedFiles.length);
          },
        });

        const responseArray = Array.isArray(response.data) ? response.data : [response.data];
        responses.push(...responseArray);
        setProcessedFiles(index + 1);
        setUploadProgress(Math.round(((index + 1) / queuedFiles.length) * 100));
      }

      duplicates = responses.filter((entry) => String(entry.message || '').includes('Duplicate filename')).length;
      setDuplicateCount(duplicates);

      const summary = summarizeUploadResults(responses, 'burn-in');
      setUploadStatus(summary);

      const latestImported = responses.find((entry) => entry.uploadId);
      if (latestImported?.uploadId) {
        await loadUploadPreview(latestImported.uploadId);
      }

      setFiles([]);
      await loadUploads();
      setUploadProgress(100);
    } catch (error) {
      console.error('Burn-in upload failed:', error);
      const details = describeRequestError(error, 'Upload failed. Please try again.');
      setUploadStatus({ success: false, ...details });
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
      setActiveFileName('');
    }
  };

  return (
    <div className={styles.container}>
      <GoToDashboardButton />

      <div className={styles.content}>
        <header className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>Burn-in intake</p>
            <h2 className={styles.title}>Upload Burn-In Files</h2>
            <p className={styles.description}>
              Import spreadsheet-based burn-in files into the dedicated burn-in database. During import, Smarti parses each measurement row, keeps normalized fields such as sequence, status, time, value, and units, and also preserves the raw row so later charts and checks can be traced back safely.
            </p>
          </div>
        </header>

        <section className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <span>Total imports</span>
            <strong>{summary?.totals?.upload_count ?? 0}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Parsed measurements</span>
            <strong>{summary?.totals?.measurement_count ?? 0}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Embedded source files</span>
            <strong>{summary?.totals?.source_file_count ?? 0}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Measured range</span>
            <strong>{summary?.totals?.first_measured_at ? `${summary.totals.first_measured_at.slice(0, 10)} to ${summary.totals.last_measured_at?.slice(0, 10) || summary.totals.first_measured_at.slice(0, 10)}` : 'Not available yet'}</strong>
          </div>
        </section>

        <div className={styles.layout}>
          <section className={styles.uploadBox}>
            <div className={styles.dropArea}>
              <button type="button" onClick={handleBrowseClick} className={styles.uploadButton}>
                Browse Burn-In Files
              </button>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv,.xls,.xlsx,.xlt,.xltx"
                onChange={handleFileChange}
                className={styles.fileInput}
              />

              <p className={styles.fileLimit}>
                Accepted formats: CSV, XLS, XLSX, XLT, XLTX. Sono supportati sia i workbook burn-in già usati nel progetto sia i CSV legacy con header tipo Step Number / Step Name / Step Result. Limite per file: 20MB.
              </p>
            </div>

            {duplicateCount > 0 && (
              <div className={styles.duplicateMessage}>
                {duplicateCount} duplicate file{duplicateCount === 1 ? '' : 's'} detected and skipped.
              </div>
            )}

            {files.length > 0 && (
              <>
                <div className={styles.actionButtons}>
                  <button onClick={handleSubmit} className={styles.uploadButton} disabled={isUploading}>
                    {isUploading ? 'Importing...' : 'Import Files'}
                  </button>
                  <button onClick={() => setFiles([])} className={styles.cancelButton} disabled={isUploading}>
                    Clear Queue
                  </button>
                </div>

                <h3 className={styles.fileListTitle}>Upload Queue</h3>
                <ul className={styles.fileList}>
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className={styles.fileItem}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileType}>{file.name.split('.').pop().toUpperCase()}</span>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>{(file.size / 1024).toFixed(2)} KB</span>
                      </div>
                      <button onClick={() => handleDeleteFile(index)} className={styles.deleteButton}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(uploadProgress > 0 || isUploading) && (
              <div className={styles.progressPanel}>
                <div className={styles.progressMeta}>
                  <span>
                    {isUploading
                      ? `Processing file ${Math.min(processedFiles + 1, files.length)} of ${files.length}`
                      : uploadProgress === 100
                        ? 'Import complete'
                        : 'Import progress'}
                  </span>
                  <strong>{uploadProgress}%</strong>
                </div>

                <div className={styles.progressBarContainer}>
                  <div className={styles.progressTrackGlow} style={{ width: `${uploadProgress}%` }} />
                  <div className={styles.progressBar} style={{ width: `${uploadProgress}%` }} />
                </div>

                <div className={styles.progressDetail}>
                  {activeFileName || `${processedFiles} of ${files.length} file${files.length === 1 ? '' : 's'} completed`}
                </div>
              </div>
            )}

            {uploadStatus && (
              <div className={uploadStatus.success ? styles.successMessage : styles.errorMessage}>
                <strong>{uploadStatus.title || uploadStatus.message}</strong>
                {uploadStatus.title ? <p>{uploadStatus.message}</p> : null}
                {uploadStatus.details ? <p className={styles.errorDetail}>{uploadStatus.details}</p> : null}
                {uploadStatus.actions?.length ? (
                  <ul className={styles.errorActionList}>
                    {uploadStatus.actions.map((action) => <li key={action}>{action}</li>)}
                  </ul>
                ) : null}
                {uploadStatus.results?.length ? (
                  <div className={styles.fileResultList}>
                    {uploadStatus.results.map((result) => (
                      <div key={`${result.filename}-${result.status}`} className={styles.fileResultItem}>
                        <span className={`${styles.fileResultBadge} ${styles[`fileResult${result.status}`] || ''}`}>{result.status}</span>
                        <strong>{result.filename}</strong>
                        <p>{result.message}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            <div className={styles.helpPanel}>
              <h3>What gets stored?</h3>
              <ul>
                <li>One row per measurement in a separate `burn_in_results.db` database.</li>
                <li>Parsed fields for sequence, status, timestamps, numeric values, and units.</li>
                <li>Raw column values are also preserved so we can build future charts safely.</li>
              </ul>
            </div>
          </section>

          <section className={styles.sidePanel}>
            <div className={styles.panelCard}>
              <h3>Recent imports</h3>
              <div className={styles.uploadList}>
                {recentUploads.length === 0 && <p className={styles.emptyState}>No burn-in files imported yet.</p>}
                {recentUploads.map((uploadItem) => (
                  <button
                    type="button"
                    key={uploadItem.id}
                    className={`${styles.uploadListItem} ${selectedUploadId === uploadItem.id ? styles.uploadListItemActive : ''}`}
                    onClick={() => loadUploadPreview(uploadItem.id)}
                  >
                    <strong>{uploadItem.original_filename}</strong>
                    <span>{uploadItem.parsed_rows} parsed rows</span>
                    <span>{uploadItem.source_file_count} embedded source file{uploadItem.source_file_count === 1 ? '' : 's'}</span>
                    <span>{uploadItem.fail_rate_pct}% fail rate</span>
                    <em>View extracted data</em>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.panelCard}>
              <h3>Top statuses</h3>
              <div className={styles.metricList}>
                {(summary?.statusCounts || []).slice(0, 6).map((statusItem) => (
                  <div key={statusItem.status} className={styles.metricRow}>
                    <span>{statusItem.status}</span>
                    <strong>{statusItem.count}</strong>
                  </div>
                ))}
                {!summary?.statusCounts?.length && <p className={styles.emptyState}>Status totals will appear after the first import.</p>}
              </div>
            </div>
          </section>
        </div>

        {selectedUpload ? (
          <div className={styles.previewModalBackdrop} onClick={closePreviewModal}>
            <section
              className={styles.previewModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="burn-in-preview-title"
              onClick={(event) => event.stopPropagation()}
            >
              <header className={styles.previewModalHeader}>
                <div>
                  <p className={styles.eyebrow}>Extracted burn-in data</p>
                  <h3 id="burn-in-preview-title">{selectedUpload.original_filename}</h3>
                  <p>
                    {selectedUpload.parsed_rows} parsed rows · {selectedUpload.source_file_count} source file{selectedUpload.source_file_count === 1 ? '' : 's'} · {selectedUpload.fail_rate_pct}% fail rate
                  </p>
                </div>
                <button type="button" className={styles.modalCloseButton} onClick={closePreviewModal}>
                  Close
                </button>
              </header>

              <div className={styles.previewModalBody}>
                <div className={styles.metadataGrid}>
                  {[
                    ['Station ID', selectedUpload.station_id],
                    ['Operator', selectedUpload.operator_name],
                    ['UUT Product Code', selectedUpload.uut_product_code],
                    ['UUT Serial Number', selectedUpload.uut_serial_number],
                    ['FIX ID', selectedUpload.fix_id],
                    ['Line', selectedUpload.line_name],
                    ['Test Socket Index', selectedUpload.test_socket_index],
                    ['Version SW', selectedUpload.version_sw],
                    ['Version HW', selectedUpload.version_hw],
                  ].filter(([, value]) => value).map(([label, value]) => (
                    <div key={label} className={styles.metadataItem}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                  {![
                    selectedUpload.station_id,
                    selectedUpload.operator_name,
                    selectedUpload.uut_product_code,
                    selectedUpload.uut_serial_number,
                    selectedUpload.fix_id,
                    selectedUpload.line_name,
                    selectedUpload.test_socket_index,
                    selectedUpload.version_sw,
                    selectedUpload.version_hw,
                  ].some(Boolean) ? (
                    <div className={styles.metadataItem}>
                      <span>Metadata</span>
                      <strong>No legacy metadata found in this file.</strong>
                    </div>
                  ) : null}
                </div>

                <div className={styles.tableWrap}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Source</th>
                        <th>Sequence</th>
                        <th>Step</th>
                        <th>Status</th>
                        <th>Measured At</th>
                        <th>Value</th>
                        <th>Units</th>
                        <th>Limit Low</th>
                        <th>Limit High</th>
                        <th>Normal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.length === 0 && (
                        <tr>
                          <td colSpan="11" className={styles.emptyTableCell}>
                            No preview rows were returned for this import.
                          </td>
                        </tr>
                      )}
                      {previewRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.row_index}</td>
                          <td>{row.source_label || '-'}</td>
                          <td>{row.sequence_text || '-'}</td>
                          <td>{row.step_name || '-'}</td>
                          <td>{row.status || '-'}</td>
                          <td>{row.measured_at || '-'}</td>
                          <td>{row.value_text || '-'}</td>
                          <td>{row.units || '-'}</td>
                          <td>{row.limit_low_raw || '-'}</td>
                          <td>{row.limit_high_raw || '-'}</td>
                          <td>{row.normal_raw || row.extra_raw || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BurnInUpload;
